import React, { useEffect, useState, useCallback } from 'react'
import { motion } from 'framer-motion'
import { useRouter } from 'next/router'
import { ArrowRight, Play, ChevronRight } from 'lucide-react'
import Button from '@/components/Button'
import Faq from '@/components/Faq'
import ThemeSwitcher from '@/components/ThemeSwitcher'
import Footer from '@/components/Footer'
import ChatIntro from '@/components/HomePage/ChatIntro'
import AIImages from '@/components/HomePage/ImageList'
import { getToken } from '@/utils/request'
import { useModel } from '@/plugin-model/useModel'
import { getSelectImages } from '@/services/ai_images'
import { Button as ShadcnButton } from '@/components/ui/button'

let requestingPage = 0

const Home = () => {
  const router = useRouter()
  const { view, viewSet } = useModel('view')
  const { invite } = router.query
  const [hasMore, setHasMore] = useState(true)
  const [images, setImages] = useState(undefined)
  const page = 1
  const pageSize = 20
  const user_id = view.user_id

  const fetchImages = useCallback(async () => {
    try {
      if (requestingPage >= page) {
        return
      }
      requestingPage = page
      const res = await getSelectImages('all', page, pageSize, user_id)
      setHasMore(false)
      setImages(res.data)
    } catch (error) {
      console.error(error)
    }
  }, [page, user_id])

  const onChange = useCallback(() => {
    setHasMore(false)
    requestingPage = 0
    fetchImages()
  }, [fetchImages])

  useEffect(() => {
    if (!getToken() && invite) {
      viewSet('loginDialogOpen', true)
    }
  }, [invite, viewSet])

  return (
    <div>
      <section className="relative overflow-hidden border-b border-border/10">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="relative mx-auto flex min-h-screen max-w-5xl flex-col items-center justify-center py-20 text-center lg:py-32">
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.1 }}
              className="mb-6 bg-gradient-to-br from-foreground via-foreground/90 to-foreground/80 bg-clip-text text-4xl font-bold tracking-tight text-transparent sm:text-5xl md:text-6xl lg:text-7xl"
            >
              释放无限创意
              <br />
              <div className="mt-4 bg-gradient-to-r from-primary via-blue-500 to-purple-600 bg-clip-text text-transparent">用 Ground AI</div>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="mb-12 max-w-2xl text-lg text-muted-foreground sm:text-xl"
            >
              突破创意边界，Ground AI 为创作者提供先进的人工智能工具， 轻松将你的想象力转化为震撼视觉作品。
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.5 }}
              className="flex flex-col gap-4 sm:flex-row sm:gap-6"
            >
              <ShadcnButton
                size="lg"
                onClick={() => router.push(`/creation/mj`)}
                className="group h-12 min-w-[180px] bg-primary px-6 text-base shadow-lg transition-all hover:bg-primary/90 hover:shadow-primary/25"
              >
                开始创作
                <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
              </ShadcnButton>

              <ShadcnButton
                variant="outline"
                size="lg"
                onClick={() => router.push('/gallery')}
                className="group h-12 min-w-[180px] border-foreground/10 px-6 text-base transition-all hover:bg-background/60"
              >
                查看示例
                <ChevronRight className="ml-1 h-4 w-4 transition-transform group-hover:translate-x-0.5" />
              </ShadcnButton>
            </motion.div>

            <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, delay: 0.7 }} className="mt-20 w-full">
              <div className="relative mx-auto aspect-[16/9] w-full max-w-4xl overflow-hidden rounded-xl border border-border/40 bg-black/5 shadow-xl dark:bg-white/5">
                <div className="absolute left-1/2 top-1/2 flex -translate-x-1/2 -translate-y-1/2 items-center justify-center">
                  <ShadcnButton size="icon" variant="secondary" className="h-16 w-16 rounded-full bg-background/80 shadow-lg transition-transform hover:scale-105">
                    <Play className="h-8 w-8 text-primary" />
                  </ShadcnButton>
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-background/80 via-transparent to-transparent" />
              </div>
            </motion.div>

            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1, delay: 0.9 }} className="mt-16 flex flex-col items-center">
              <p className="mb-6 text-sm text-muted-foreground">受到行业领导者的信任</p>
              <div className="flex flex-wrap items-center justify-center gap-x-12 gap-y-6 opacity-70 grayscale">
                {['ChatGPT', 'Claude', 'DeepSeek', 'Midjourney', 'DALLE'].map((v) => (
                  <div key={v} className="flex h-8 w-24 items-center justify-center rounded-md bg-foreground/10 text-xs text-muted-foreground">
                    {v}
                  </div>
                ))}
              </div>
            </motion.div>

            <div className="mx-auto mt-24 grid max-w-4xl grid-cols-2 gap-8 md:grid-cols-4">
              {[
                { label: '创作者', value: '100,000+' },
                { label: '作品', value: '500万+' },
                { label: '每日创作', value: '50,000+' },
                { label: '满意度', value: '99.8%' },
              ].map((stat, i) => (
                <motion.div
                  key={+i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.1 }}
                  className="flex flex-col items-center"
                >
                  <div className="text-2xl font-bold sm:text-3xl">{stat.value}</div>
                  <div className="text-sm text-muted-foreground">{stat.label}</div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <div className="m-auto w-[1440px] max-w-full p-4">
        <ChatIntro />
      </div>

      <div className="m-auto w-[1440px] max-w-full p-4">
        <div className="mb-4 mt-12 text-center text-3xl lg:text-4xl">AI智能绘画</div>
        <div className="text-fill-transparent m-auto mb-12 bg-gradient-to-r from-sky-500 to-primary bg-clip-text text-center">
          能够生成艺术作品、转换艺术风格、处理图像，并创作虚拟场景，是您创作的最佳助手。
        </div>
        <AIImages images={images} onChange={onChange} hasMore={hasMore} setImages={setImages} />
        <div className="mt-12 text-center">
          <Button onClick={() => router.push('/gallery')}>查看更多画作</Button>
        </div>
      </div>
      <div className="m-auto w-[1440px] max-w-full p-4">
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          whileInView={{
            y: 0,
            opacity: 1,
          }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="m-auto w-[960px] max-w-full px-2"
        >
          <Faq />
        </motion.div>
      </div>
      <Footer />
      <div className="fixed bottom-4 right-4 z-30 flex flex-col items-center justify-center gap-2">
        <ThemeSwitcher />
      </div>
    </div>
  )
}

export default Home
